// P0-7 (AB#2047) — TanStack Query read hook for the SuperAdmin read-only
// recent-bookings panel. Wraps
// GET /api/super-admin/businesses/{businessId}/appointments via
// apiService.getBusinessAppointments. Keyed by businessId + filters and enabled
// only when a business is selected, mirroring the usePackages read pattern.
// Strictly read-only — no mutations.

import { useQuery } from '@tanstack/react-query';
import {
    apiService,
    type AdminAppointmentListResponse,
    type GetBusinessAppointmentsParams,
} from '../services/api';
import { queryKeys } from '../lib/queryKeys';

export interface UseAdminBusinessAppointmentsArgs extends GetBusinessAppointmentsParams {
    /** Selected business; the query stays disabled until this is set. */
    businessId: number | null;
}

/**
 * Fetch a page of a business's appointments for SuperAdmin pilot support.
 * The query is disabled while `businessId` is null so opening the panel with no
 * selection issues no request.
 */
export function useAdminBusinessAppointments({
    businessId,
    customerProfileId,
    from,
    to,
    pageNumber,
    pageSize,
}: UseAdminBusinessAppointmentsArgs) {
    const filters: GetBusinessAppointmentsParams = {
        customerProfileId,
        from,
        to,
        pageNumber,
        pageSize,
    };

    return useQuery<AdminAppointmentListResponse>({
        queryKey: queryKeys.adminBookings.list(businessId ?? 0, filters as Record<string, unknown>),
        queryFn: () => apiService.getBusinessAppointments(businessId as number, filters),
        enabled: businessId != null,
        staleTime: 30_000,
    });
}
